import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { FiBriefcase, FiSearch, FiDownload, FiPrinter } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getJobsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { addPrintBrand } from '../../utils/printBranding';

const formatCurrency = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-PK', { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(amount);
};

// Job P&L = account-type (COA) driven: 4xxxx revenue, 5xxxx expense, rest ignored.
const classifyAccount = (accountType, code) => {
  const at = (accountType || '').toLowerCase();
  if (at.startsWith('revenue')) return 'revenue';
  if (at.startsWith('expens')) return 'expense';
  const c = (code || '').charAt(0);
  if (c === '4') return 'revenue';
  if (c === '5') return 'expense';
  return 'other';
};

const ZERO = { revenue: 0, cost: 0, profit: 0, avgMargin: 0, jobCount: 0, overBudget: 0 };

const JobProfitLoss = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();

  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFYId, setSelectedFYId] = useState('');
  const [jobs, setJobs] = useState([]);
  const [customersMap, setCustomersMap] = useState({});
  const [accountsMap, setAccountsMap] = useState({});
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('profit');

  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(ZERO);

  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const snapshot = await getDocs(query(collection(db, 'companies', activeCompany.id, 'financialYears'), orderBy('startDate', 'desc')));
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);
      if (activeFinancialYear?.id) setSelectedFYId(activeFinancialYear.id);
      else if (fys.length > 0) setSelectedFYId(fys[0].id);
    } catch (e) { console.error('Error loading financial years:', e); }
  }, [activeCompany, activeFinancialYear]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const jobRows = await getJobsCached(activeCompany.id);
      setJobs(jobRows.filter(r => r.jobCode != null)
        .sort((a, b) => String(a.jobCode || '').localeCompare(String(b.jobCode || ''))));
    } catch (e) { console.error('Error loading jobs:', e); }
  }, [activeCompany]);

  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const snapshot = await getDocs(collection(db, 'companies', activeCompany.id, 'customers'));
      const map = {};
      snapshot.docs.forEach(d => { map[d.id] = d.data().companyName || d.data().contactPerson || ''; });
      setCustomersMap(map);
    } catch (e) { console.error('Error loading customers:', e); }
  }, [activeCompany]);

  const loadAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const snapshot = await getDocs(collection(db, 'companies', activeCompany.id, 'accounts'));
      const map = {};
      snapshot.docs.forEach(d => { const a = d.data(); map[d.id] = { code: a.code, accountType: a.accountType }; });
      setAccountsMap(map);
    } catch (e) { console.error('Error loading accounts:', e); }
  }, [activeCompany]);

  useEffect(() => {
    loadFinancialYears(); loadJobs(); loadCustomers(); loadAccounts();
  }, [loadFinancialYears, loadJobs, loadCustomers, loadAccounts]);

  // Customers that actually appear on jobs (for the filter dropdown)
  const jobCustomers = useMemo(() => {
    const seen = new Map();
    jobs.forEach(j => { if (j.customerId && !seen.has(j.customerId)) seen.set(j.customerId, customersMap[j.customerId] || j.customerId); });
    return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [jobs, customersMap]);

  const generate = async () => {
    if (!activeCompany?.id) return;
    if (!selectedFYId) { alert('Please select a financial year'); return; }
    setLoading(true);
    setHasSearched(true);
    try {
      const jobsFiltered = jobs.filter(j => {
        if (customerFilter && (j.customerId || '') !== customerFilter) return false;
        if (statusFilter !== 'all' && (j.status || '') !== statusFilter) return false;
        return true;
      });
      if (jobsFiltered.length === 0) { setRows([]); setSummary(ZERO); setLoading(false); return; }

      // 1. Per-job transaction queries (single equality filter each, no index needed), in parallel.
      const txRef = collection(db, 'companies', activeCompany.id, 'transactions');
      const perJob = await Promise.all(jobsFiltered.map(async (j) => {
        const snap = await getDocs(query(txRef, where('jobId', '==', j.id)));
        const txs = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(t => t.financialYearId === selectedFYId)
          .filter(t => {
            if (!activeBranch || activeBranch.id === 'all') return true;
            return (t.branchId || null) === activeBranch.id;
          })
          .filter(t => {
            if (dateFrom && (t.docDate || '') < dateFrom) return false;
            if (dateTo && (t.docDate || '') > dateTo) return false;
            return true;
          });
        return { jobId: j.id, txs };
      }));

      const txJob = {};
      const allTxIds = [];
      perJob.forEach(({ jobId, txs }) => txs.forEach(t => { txJob[t.id] = jobId; allTxIds.push(t.id); }));

      // 2. Their accounting lines, chunked by transactionId (10 per 'in'), in parallel.
      const subRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
      const chunks = [];
      for (let i = 0; i < allTxIds.length; i += 10) chunks.push(allTxIds.slice(i, i + 10));
      const subResults = await Promise.all(chunks.map(c => getDocs(query(subRef, where('transactionId', 'in', c)))));
      const subs = [];
      subResults.forEach(s => s.docs.forEach(d => subs.push({ id: d.id, ...d.data() })));

      // 3. Accumulate revenue/cost per job by account-type classification.
      const acc = {};
      jobsFiltered.forEach(j => { acc[j.id] = { revenue: 0, cost: 0 }; });
      subs.filter(s => s.status === 'Posted').forEach(s => {
        const jobId = txJob[s.transactionId];
        if (!jobId || !acc[jobId]) return;
        const a = accountsMap[s.accountId];
        const cls = classifyAccount(a?.accountType, a?.code || s.accountCode);
        const debit = s.debitAmount || 0;
        const credit = s.creditAmount || 0;
        if (cls === 'revenue') acc[jobId].revenue += credit - debit;
        else if (cls === 'expense') acc[jobId].cost += debit - credit;
      });

      const builtRows = jobsFiltered.map(j => {
        const { revenue, cost } = acc[j.id];
        const profit = revenue - cost;
        const budget = Number(j.budgetAmount) || 0;
        return {
          id: j.id,
          jobCode: j.jobCode || '',
          jobName: j.jobName || '',
          customerName: customersMap[j.customerId] || '',
          status: j.status || '',
          revenue, cost, profit,
          margin: revenue !== 0 ? (profit / revenue) * 100 : 0,
          budget,
          hasBudget: budget > 0,
          budgetVariance: budget > 0 ? budget - cost : 0,
        };
      });

      const tR = builtRows.reduce((s, r) => s + r.revenue, 0);
      const tC = builtRows.reduce((s, r) => s + r.cost, 0);
      const tP = tR - tC;
      setRows(builtRows);
      setSummary({
        revenue: tR,
        cost: tC,
        profit: tP,
        avgMargin: tR !== 0 ? (tP / tR) * 100 : 0,
        jobCount: builtRows.length,
        overBudget: builtRows.filter(r => r.hasBudget && r.cost > r.budget).length,
      });
    } catch (e) {
      console.error('Error generating job P&L:', e);
      alert('Error generating report. See console for details.');
    } finally {
      setLoading(false);
    }
  };

  const sortedRows = useMemo(() => {
    const r = [...rows];
    const cmp = {
      code: (a, b) => (a.jobCode || '').localeCompare(b.jobCode || ''),
      revenue: (a, b) => b.revenue - a.revenue,
      cost: (a, b) => b.cost - a.cost,
      profit: (a, b) => b.profit - a.profit,
      margin: (a, b) => b.margin - a.margin,
    }[sortBy] || ((a, b) => 0);
    return r.sort(cmp);
  }, [rows, sortBy]);

  const handleExport = () => {
    if (!hasSearched || rows.length === 0) return;
    const fy = financialYears.find(f => f.id === selectedFYId);
    let csv = `Job Profit & Loss\nCompany: ${activeCompany?.companyName || ''}\nFinancial Year: ${fy?.yearName || fy?.yearCode || ''}\n`;
    if (dateFrom || dateTo) csv += `Period: ${dateFrom || 'start'} to ${dateTo || 'end'}\n`;
    csv += `\nJob Code,Job Name,Customer,Status,Revenue,Cost,Gross Profit,Margin %,Budget,Budget Variance\n`;
    sortedRows.forEach(r => {
      csv += `${r.jobCode},"${(r.jobName || '').replace(/"/g, '""')}","${(r.customerName || '').replace(/"/g, '""')}",${r.status},${r.revenue},${r.cost},${r.profit},${r.margin.toFixed(2)},${r.budget},${r.hasBudget ? r.budgetVariance : ''}\n`;
    });
    csv += `\nTotals,,,,${summary.revenue},${summary.cost},${summary.profit},${summary.avgMargin.toFixed(2)},,\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `JobProfitLoss.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (!hasSearched || rows.length === 0) return;
    const fy = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Job Profit & Loss</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        h1{color:#800020;font-size:20px;margin-bottom:4px}
        .meta{color:#666;font-size:12px;margin-bottom:14px}
        .kpis{display:flex;flex-wrap:wrap;gap:20px;padding:14px;background:#f9f9f9;border-radius:8px;margin-bottom:12px}
        .kpi{text-align:center}.kpi .l{font-size:11px;color:#666}.kpi .v{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse}
        th{background:#800020;color:#fff;padding:8px;text-align:left;font-size:11px;text-transform:uppercase}
        td{padding:7px 8px;border-bottom:1px solid #eee}
        .amount{text-align:right;font-family:'Consolas',monospace}
        tr.total td{background:#efe6e9;font-weight:700}
        @media print{body{margin:15px}}
      </style></head><body>
      <h1>Job Profit &amp; Loss</h1>
      <div class="meta">${activeCompany?.companyName || ''} | FY: ${fy?.yearName || fy?.yearCode || ''}${(dateFrom || dateTo) ? ' | Period: ' + (dateFrom || 'start') + ' to ' + (dateTo || 'end') : ''}</div>
      <div class="kpis">
        <div class="kpi"><div class="l">Total Revenue</div><div class="v">${formatCurrency(summary.revenue)}</div></div>
        <div class="kpi"><div class="l">Total Cost</div><div class="v">${formatCurrency(summary.cost)}</div></div>
        <div class="kpi"><div class="l">Total Profit</div><div class="v">${formatCurrency(summary.profit)}</div></div>
        <div class="kpi"><div class="l">Avg Margin</div><div class="v">${summary.avgMargin.toFixed(1)}%</div></div>
        <div class="kpi"><div class="l">Jobs</div><div class="v">${summary.jobCount}</div></div>
        <div class="kpi"><div class="l">Over Budget</div><div class="v">${summary.overBudget}</div></div>
      </div>
      <table>
        <thead><tr><th>Code</th><th>Job</th><th>Customer</th><th>Status</th><th class="amount">Revenue</th><th class="amount">Cost</th><th class="amount">Profit</th><th class="amount">Margin %</th><th class="amount">Budget</th><th class="amount">Budget Var.</th></tr></thead>
        <tbody>
          ${sortedRows.map(r => `<tr>
            <td>${r.jobCode}</td><td>${r.jobName}</td><td>${r.customerName || '-'}</td><td>${r.status || '-'}</td>
            <td class="amount">${formatCurrency(r.revenue)}</td>
            <td class="amount">${formatCurrency(r.cost)}</td>
            <td class="amount">${formatCurrency(r.profit)}</td>
            <td class="amount">${r.revenue !== 0 ? r.margin.toFixed(1) + '%' : '-'}</td>
            <td class="amount">${r.hasBudget ? formatCurrency(r.budget) : '-'}</td>
            <td class="amount">${r.hasBudget ? formatCurrency(r.budgetVariance) : '-'}</td>
          </tr>`).join('')}
          <tr class="total"><td colspan="4">Totals</td><td class="amount">${formatCurrency(summary.revenue)}</td><td class="amount">${formatCurrency(summary.cost)}</td><td class="amount">${formatCurrency(summary.profit)}</td><td class="amount">${summary.avgMargin.toFixed(1)}%</td><td class="amount"></td><td class="amount"></td></tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  return (
    <PageLayout pageTitle="Job Profit & Loss" breadcrumb="Reports / Job Reports / Job P&L">
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year *</Label>
            <Select value={selectedFYId} onChange={(e) => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>{fy.yearName || fy.yearCode}{fy.isActive ? ' (Active)' : ''}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Customer</Label>
            <Select value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}>
              <option value="">All Customers</option>
              {jobCustomers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Status</Label>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="Active">Active</option>
              <option value="On Hold">On Hold</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>From Date</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>To Date</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Sort By</Label>
            <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="profit">Profit (high → low)</option>
              <option value="revenue">Revenue (high → low)</option>
              <option value="cost">Cost (high → low)</option>
              <option value="margin">Margin (high → low)</option>
              <option value="code">Job Code</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>
        <ButtonGroup>
          <Button onClick={handleExport} disabled={!hasSearched || rows.length === 0}><FiDownload /> Export CSV</Button>
          <Button onClick={handlePrint} disabled={!hasSearched || rows.length === 0}><FiPrinter /> Print</Button>
          <Button primary onClick={generate} disabled={loading}><FiSearch /> {loading ? 'Loading...' : 'Generate Report'}</Button>
        </ButtonGroup>
      </FiltersCard>

      {hasSearched && !loading && (
        <KpiGrid>
          <Kpi><KpiLabel>Total Revenue</KpiLabel><KpiValue $pos>{formatCurrency(summary.revenue)}</KpiValue></Kpi>
          <Kpi><KpiLabel>Total Cost</KpiLabel><KpiValue $neg>{formatCurrency(summary.cost)}</KpiValue></Kpi>
          <Kpi><KpiLabel>Total Profit</KpiLabel><KpiValue $pos={summary.profit >= 0} $neg={summary.profit < 0}>{formatCurrency(summary.profit)}</KpiValue></Kpi>
          <Kpi><KpiLabel>Avg Margin</KpiLabel><KpiValue>{summary.avgMargin.toFixed(1)}%</KpiValue></Kpi>
          <Kpi><KpiLabel>Jobs</KpiLabel><KpiValue>{summary.jobCount}</KpiValue></Kpi>
          <Kpi><KpiLabel>Over Budget</KpiLabel><KpiValue $neg={summary.overBudget > 0}>{summary.overBudget}</KpiValue></Kpi>
        </KpiGrid>
      )}

      <ContentCard>
        {!hasSearched ? (
          <EmptyState>
            <EmptyIcon><FiBriefcase /></EmptyIcon>
            <EmptyText>Select a financial year and generate to see job profitability</EmptyText>
          </EmptyState>
        ) : loading ? (
          <EmptyState><EmptyText>Loading...</EmptyText></EmptyState>
        ) : rows.length === 0 ? (
          <EmptyState>
            <EmptyIcon><FiBriefcase /></EmptyIcon>
            <EmptyText>No jobs match the selected filters</EmptyText>
          </EmptyState>
        ) : (
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th>Code</Th>
                  <Th>Job</Th>
                  <Th>Customer</Th>
                  <Th>Status</Th>
                  <Th align="right">Revenue</Th>
                  <Th align="right">Cost</Th>
                  <Th align="right">Gross Profit</Th>
                  <Th align="right">Margin %</Th>
                  <Th align="right">Budget</Th>
                  <Th align="right">Budget Var.</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedRows.map(r => (
                  <Tr key={r.id}>
                    <Td>{r.jobCode}</Td>
                    <Td>{r.jobName}</Td>
                    <Td>{r.customerName || '-'}</Td>
                    <Td><StatusBadge $status={r.status}>{r.status || '-'}</StatusBadge></Td>
                    <AmountCell align="right" $pos>{formatCurrency(r.revenue)}</AmountCell>
                    <AmountCell align="right" $neg>{formatCurrency(r.cost)}</AmountCell>
                    <AmountCell align="right" $pos={r.profit >= 0} $neg={r.profit < 0}>{formatCurrency(r.profit)}</AmountCell>
                    <AmountCell align="right">{r.revenue !== 0 ? `${r.margin.toFixed(1)}%` : '-'}</AmountCell>
                    <AmountCell align="right">{r.hasBudget ? formatCurrency(r.budget) : '-'}</AmountCell>
                    <AmountCell align="right" $pos={r.hasBudget && r.budgetVariance >= 0} $neg={r.hasBudget && r.budgetVariance < 0}>{r.hasBudget ? formatCurrency(r.budgetVariance) : '-'}</AmountCell>
                  </Tr>
                ))}
                <Tr $total>
                  <Td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>Totals</Td>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>{formatCurrency(summary.revenue)}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>{formatCurrency(summary.cost)}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>{formatCurrency(summary.profit)}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>{summary.avgMargin.toFixed(1)}%</AmountCell>
                  <AmountCell align="right">-</AmountCell>
                  <AmountCell align="right">-</AmountCell>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </ContentCard>
    </PageLayout>
  );
};

// ---- Styled Components ----
const FiltersCard = styled.div`
  background: white; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm}; padding: 20px; margin-bottom: 20px;
`;
const FiltersGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 16px; margin-bottom: 16px;
`;
const FilterGroup = styled.div`
  display: flex; flex-direction: column; gap: 6px;
`;
const Label = styled.label`
  font-size: 12px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase;
`;
const Select = styled.select`
  padding: 10px 12px; border: 1px solid ${colors.neutral[300]}; border-radius: ${colors.borderRadius.md}; font-size: 14px; color: ${colors.text.primary}; background: white; cursor: pointer;
  &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; }
`;
const Input = styled.input`
  padding: 10px 12px; border: 1px solid ${colors.neutral[300]}; border-radius: ${colors.borderRadius.md}; font-size: 14px; color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; }
`;
const ButtonGroup = styled.div`
  display: flex; gap: 12px; justify-content: flex-end;
`;
const Button = styled.button`
  padding: 10px 20px; border: none; border-radius: ${colors.borderRadius.md}; font-size: 14px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: all 0.2s;
  ${props => props.primary ? `
    background: ${colors.primary.main}; color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: ${colors.neutral[100]}; color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[200]}; }
  `}
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const KpiGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 16px; margin-bottom: 20px;
`;
const Kpi = styled.div`
  background: white; padding: 16px; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm};
`;
const KpiLabel = styled.div`
  font-size: 12px; color: ${colors.text.secondary}; margin-bottom: 6px;
`;
const KpiValue = styled.div`
  font-size: 20px; font-weight: 600; color: ${props => props.$pos ? '#2E7D32' : props.$neg ? '#C62828' : colors.text.primary};
`;
const ContentCard = styled.div`
  background: white; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm}; overflow: hidden;
`;
const TableContainer = styled.div`
  overflow-x: auto;
`;
const Table = styled.table`
  width: 100%; border-collapse: collapse;
`;
const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;
const Th = styled.th`
  padding: 12px 14px; text-align: ${props => props.align || 'left'}; font-size: 11px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid ${colors.neutral[200]}; white-space: nowrap;
`;
const Tbody = styled.tbody``;
const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  &:hover { background: ${colors.neutral[50]}; }
  ${props => props.$total && `background: ${colors.primary.main}0D; font-weight: 600;`}
`;
const Td = styled.td`
  padding: 10px 14px; font-size: 13px; color: ${colors.text.primary}; text-align: ${props => props.align || 'left'};
`;
const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace; font-size: 13px; text-align: right;
  color: ${props => props.$pos ? '#2E7D32' : props.$neg ? '#C62828' : colors.text.primary};
`;
const StatusBadge = styled.span`
  display: inline-block; padding: 3px 8px; border-radius: ${colors.borderRadius.sm}; font-size: 11px; font-weight: 500;
  background: ${props => {
    const s = props.$status;
    if (s === 'Active') return '#E8F5E9';
    if (s === 'Completed') return '#E3F2FD';
    if (s === 'On Hold') return '#FFF3E0';
    if (s === 'Cancelled') return '#FFEBEE';
    return colors.neutral[200];
  }};
  color: ${props => {
    const s = props.$status;
    if (s === 'Active') return '#2E7D32';
    if (s === 'Completed') return '#1565C0';
    if (s === 'On Hold') return '#E65100';
    if (s === 'Cancelled') return '#C62828';
    return colors.text.secondary;
  }};
`;
const EmptyState = styled.div`
  text-align: center; padding: 60px 20px; color: ${colors.text.secondary};
`;
const EmptyIcon = styled.div`
  font-size: 48px; color: ${colors.neutral[300]}; margin-bottom: 16px;
`;
const EmptyText = styled.p`
  font-size: 16px; margin-bottom: 8px;
`;

export default JobProfitLoss;
